import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { environment } from '../app/app.environment';
import { DashboardPdfService } from '../app/shared/services/dashboard-pdf.service';
import { DynamicTemplateConfigService } from './pdf-generation/dynamic-template-config.service';

export interface WhatsappInvoiceDetails {
  invoiceNo: string;
  phone?: string | number | null;
  customerName?: string | null;
  storeName?: string | null;
  invoiceDate?: string | null;
  amount?: number | string | null;
}

export interface WhatsappInvoicePayload {
  blob: Blob;
  details: WhatsappInvoiceDetails;
}

/**
 * sent = delivered to Meta via the WhatsApp Cloud API, shared = sent via share sheet,
 * cancelled = user closed the share sheet, downloaded = PDF saved + WhatsApp Web opened,
 * chat-only = PDF unavailable, WhatsApp Web opened with the message only
 */
export type WhatsappShareStatus = 'sent' | 'shared' | 'cancelled' | 'downloaded' | 'chat-only';

export interface WhatsappShareResult {
  status: WhatsappShareStatus;
  /** recipient number (with country code) when sent via the API */
  to?: string;
}

/** Screen the share started from; recorded in the backend share log */
export type WhatsappShareModule = 'FO_DASHBOARD' | 'FO_SALES_HISTORY';

/**
 * Sends an invoice PDF on WhatsApp.
 * When the backend has the WhatsApp Cloud API configured, the PDF is posted to the backend and Meta delivers it.
 * Otherwise: native share sheet with the PDF attached where the browser supports it,
 * else downloads the PDF and opens the customer's chat on web.whatsapp.com.
 */
// Provided by DashboardComponent (not root) so it shares the dashboard's DashboardPdfService instance and its last-invoice cache
@Injectable()
export class WhatsappShareService {
  private readonly defaultCountryCode = '91';
  private readonly saleApiUrl = `${environment.apiURL}v1/sale`;
  private apiStatus: Promise<boolean> | null = null;
  /** true once Super Admin has enabled WhatsApp sharing for this business; buttons stay hidden otherwise */
  allowed = false;
  /** true once the backend reports the WhatsApp Cloud API as configured (and allowed) */
  apiEnabled = false;

  constructor(
    private http: HttpClient,
    private dashboardPdfService: DashboardPdfService,
    private dynamicTemplateConfigService: DynamicTemplateConfigService,
  ) {}

  /**
   * Call on screen init so clicks can pick API vs fallback synchronously.
   * Not cached: called again whenever the sales list reloads, so a Super Admin change shows without a re-login.
   */
  loadApiStatus(): Promise<boolean> {
    this.apiStatus = firstValueFrom(this.http.get<any>(`${this.saleApiUrl}/whatsapp/status`))
      .then((response) => response?.data ?? {})
      .catch(() => ({}))
      .then((data) => {
        this.allowed = data.allowed === true;
        this.apiEnabled = data.enabled === true;
        return this.apiEnabled;
      });
    return this.apiStatus;
  }

  normalizePhone(raw: string | number | null | undefined): string {
    const digits = String(raw ?? '').replace(/\D/g, '').replace(/^0+/, '');
    if (digits.length === 10) return `${this.defaultCountryCode}${digits}`;
    return digits.length >= 11 && digits.length <= 15 ? digits : '';
  }

  buildMessage(details: WhatsappInvoiceDetails): string {
    const name = details.customerName && !/walk-?in/i.test(details.customerName) ? ` ${details.customerName}` : '';
    const store = details.storeName ? ` at ${details.storeName}` : '';
    const date = details.invoiceDate ? ` dated ${details.invoiceDate}` : '';
    const amount = Number(details.amount);
    const amountText = Number.isFinite(amount)
      ? `, amount ₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : '';
    return `Hi${name}, thank you for shopping${store}. Invoice ${details.invoiceNo}${date}${amountText}. Please find the invoice attached.`;
  }

  canShareFiles(): boolean {
    try {
      const probe = new File([''], 'invoice.pdf', { type: 'application/pdf' });
      return !!navigator.canShare && navigator.canShare({ files: [probe] });
    } catch {
      return false;
    }
  }

  /**
   * Must be called directly from a click handler: the share sheet / new tab need the user gesture.
   * load may be slow (PDF generation or fetch); the tab is pre-opened so popup blockers don't interfere.
   * If load fails and fallbackDetails is given, WhatsApp Web is still opened with the message.
   */
  async shareInvoice(
    load: () => Promise<WhatsappInvoicePayload>,
    fallbackDetails?: WhatsappInvoiceDetails,
    sendUrl?: string,
    module?: WhatsappShareModule,
  ): Promise<WhatsappShareResult> {
    if (this.apiEnabled && sendUrl) {
      return this.sendViaApi(load, sendUrl, module);
    }

    const useShareSheet = this.canShareFiles();
    const chatWindow = useShareSheet ? null : window.open('', '_blank');

    let payload: WhatsappInvoicePayload;
    try {
      payload = await load();
    } catch (error) {
      if (!fallbackDetails) {
        chatWindow?.close();
        throw error;
      }
      console.warn('[WhatsApp] Invoice PDF unavailable, opening chat with message only:', error);
      this.openChat(fallbackDetails.phone, this.buildMessage(fallbackDetails), chatWindow);
      return { status: 'chat-only' };
    }

    const { blob, details } = payload;
    const fileName = this.fileName(details);
    const text = this.buildMessage(details);

    if (useShareSheet) {
      try {
        await navigator.share({ files: [new File([blob], fileName, { type: 'application/pdf' })], text });
        return { status: 'shared' };
      } catch (error: any) {
        if (error?.name === 'AbortError') return { status: 'cancelled' };
        // NotAllowedError (gesture expired while loading the PDF) etc. -> fall back to download + WhatsApp Web
      }
    }

    this.downloadBlob(blob, fileName);
    this.openChat(details.phone, text, chatWindow);
    return { status: 'downloaded' };
  }

  /**
   * Shares a sales-history row. Uses the PDF of the sale just completed on this till when it matches,
   * otherwise fetches the invoice PDF stored in Azure (needs Blob CORS to allow GET from the POS origin).
   */
  async shareTransaction(transaction: any, messageService: MessageService, module: WhatsappShareModule): Promise<void> {
    if (!transaction || !this.allowed) return;

    let storename: any = {};
    try {
      storename = JSON.parse(localStorage.getItem('storename') || '{}');
    } catch {
      storename = {};
    }
    const invoiceDate = transaction.invoicedate ? new Date(transaction.invoicedate) : null;
    const details: WhatsappInvoiceDetails = {
      invoiceNo: transaction.invoiceno,
      phone: transaction.customer?.mobile_no || transaction.user_id,
      customerName: transaction.name,
      storeName: this.dynamicTemplateConfigService.getClientData()?.client_name || storename?.name,
      invoiceDate: invoiceDate && !Number.isNaN(invoiceDate.getTime())
        ? invoiceDate.toLocaleDateString('en-GB').replace(/\//g, '-')
        : '',
      amount: transaction.amount,
    };

    try {
      const result = await this.shareInvoice(async () => {
        const cachedBlob = await this.dashboardPdfService.getLastInvoicePdfBlob(transaction.invoiceno);
        if (cachedBlob) return { blob: cachedBlob, details };
        if (!transaction.invoicepdf) throw new Error('Invoice PDF is not available yet');
        const response = await fetch(transaction.invoicepdf, { mode: 'cors', credentials: 'omit' });
        if (!response.ok) throw new Error(`Invoice PDF download failed (${response.status})`);
        return { blob: await response.blob(), details };
      }, details, `${this.saleApiUrl}/whatsapp/${transaction.transactionUuid}`, module);

      if (result.status !== 'sent') {
        // chat-only opens WhatsApp with the text alone, the other fallbacks attach the PDF
        const fileName = result.status === 'chat-only' ? undefined : this.fileName(details);
        this.logShare(transaction.transactionUuid, module, result.status, details.phone, this.buildMessage(details), fileName);
      }

      if (result.status === 'sent') {
        messageService.add({
          severity: 'success',
          summary: 'Sent on WhatsApp',
          detail: `Invoice sent to +${result.to}`,
          life: 3000,
        });
      } else if (result.status === 'downloaded') {
        messageService.add({
          severity: 'info',
          summary: 'Invoice Downloaded',
          detail: 'Attach the downloaded invoice in the WhatsApp chat',
          life: 4000,
        });
      } else if (result.status === 'chat-only') {
        messageService.add({
          severity: 'warn',
          summary: 'PDF Not Attached',
          detail: 'Could not load the invoice PDF. Download it from Sales History and attach it in WhatsApp.',
          life: 5000,
        });
      }
    } catch (error) {
      console.error('[WhatsApp] Error sharing invoice:', error);
      messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: error instanceof Error ? error.message : 'Failed to share invoice on WhatsApp',
        life: 3000,
      });
    }
  }

  /** Backend uploads the PDF to Meta and sends the approved invoice template; on failure the PDF is downloaded */
  private async sendViaApi(
    load: () => Promise<WhatsappInvoicePayload>,
    sendUrl: string,
    module?: WhatsappShareModule,
  ): Promise<WhatsappShareResult> {
    const { blob, details } = await load();
    const fileName = this.fileName(details);
    const form = new FormData();
    form.append('file', blob, fileName);
    if (module) form.append('module', module);

    try {
      const response = await firstValueFrom(this.http.post<any>(sendUrl, form));
      return { status: 'sent', to: response?.data?.to };
    } catch (error: any) {
      this.downloadBlob(blob, fileName);
      const reason = error?.error?.message || 'WhatsApp send failed';
      throw new Error(`${reason}. Invoice downloaded instead.`);
    }
  }

  /** Records a share-sheet / WhatsApp Web share; Cloud API sends are logged by the backend itself */
  private logShare(
    saleUuid: string | undefined,
    module: WhatsappShareModule,
    status: WhatsappShareStatus,
    phone: string | number | null | undefined,
    content: string,
    fileName?: string,
  ): void {
    if (!saleUuid) return;
    this.http
      .post(`${this.saleApiUrl}/whatsapp/log/${saleUuid}`, {
        module,
        status: status.toUpperCase().replace('-', '_'),
        phone: phone ?? null,
        content,
        file_name: fileName ?? null,
      })
      .subscribe({ error: (error) => console.warn('[WhatsApp] Share log failed:', error) });
  }

  openChat(phone: string | number | null | undefined, text: string, targetWindow?: Window | null): void {
    const number = this.normalizePhone(phone);
    const url = `https://web.whatsapp.com/send?${number ? `phone=${number}&` : ''}text=${encodeURIComponent(text)}`;
    if (targetWindow) {
      targetWindow.opener = null;
      targetWindow.location.href = url;
    } else {
      window.open(url, '_blank', 'noopener');
    }
  }

  private fileName(details: WhatsappInvoiceDetails): string {
    return `Invoice-${String(details.invoiceNo || 'invoice').replace(/[^\w-]+/g, '_')}.pdf`;
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  }
}
